import { Skeleton } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PublicStatusPage from '../../common/PublicStatusPage/PublicStatusPage';
import { apiPublicSubscriberDetail } from '../../../api/subscription';
import { formatDate, formatPrice } from '../../../utils/format';
import { frequencyNoun, paymentStatusMeta } from '../../../utils/subscriptions';
import './SubscriberSelfService.scss';

const SubscriberSelfService = () => {
  const { subscriberId } = useParams();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiPublicSubscriberDetail(subscriberId);
      setPayload(data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [subscriberId]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const isNotFound = error?.code === 'subscriber_not_found' || error?.status === 404;

  const showCancelLink = payload?.state !== 'canceled' && Boolean(payload?.cancel_url);
  const hasFooterContent = showCancelLink || Boolean(payload?.merchant?.email) || Boolean(payload?.merchant?.phone);

  const retryAction = { label: 'Tentar novamente', onClick: fetchDetail };
  const paymentFallbackAction = retryAction;
  const payAction = (payment) => (payment?.pay_url ? { label: 'Pagar agora', to: payment.pay_url } : null);

  const suspendedBanner = (currentPayment) => {
    const amountText = payload?.amount_due != null ? formatPrice(payload.amount_due) : null;
    const attempts = currentPayment?.attempts_allowed;
    const chargeClause = amountText
      ? `Não conseguimos concluir a cobrança de ${amountText}`
      : 'Não conseguimos concluir a cobrança da sua assinatura';
    const attemptsClause = attempts != null ? ` após ${attempts} tentativas` : '';
    return {
      modifier: 'danger',
      heading: 'Sua assinatura foi suspensa',
      body: `${chargeClause}${attemptsClause}.`,
      action: currentPayment?.pay_url ? { label: 'Reativar assinatura', to: currentPayment.pay_url } : paymentFallbackAction,
    };
  };

  const getBanner = () => {
    if (!payload) {
      return null;
    }

    const payments = payload.open_payments || [];
    const earliest = payments[0] || null;
    const firstFailed = payments.find((p) => p.payment_status === 'failed') || null;
    const exhausted =
      payments.find((p) => p.payment_status === 'failed' && p.attempts_made >= p.attempts_allowed) || null;
    const currentPayment = exhausted || firstFailed || earliest;

    switch (payload.state) {
      case 'active': {
        const amountText = payload.plan?.amount != null ? formatPrice(payload.plan.amount) : null;
        const nextBillingDateText = payload.next_billing_date ? formatDate(payload.next_billing_date) : null;
        let body = 'Sua assinatura está ativa.';
        if (amountText && nextBillingDateText) {
          body = `A próxima cobrança de ${amountText} acontece em ${nextBillingDateText}.`;
        } else if (amountText) {
          body = `O valor da próxima cobrança é ${amountText}.`;
        } else if (nextBillingDateText) {
          body = `Sua próxima cobrança acontece em ${nextBillingDateText}.`;
        }
        return {
          modifier: 'success',
          heading: 'Sua assinatura está em dia',
          body,
          action: null,
        };
      }
      case 'due': {
        const amountText = payload.amount_due != null ? formatPrice(payload.amount_due) : null;
        const dueDateText = currentPayment?.due_date ? formatDate(currentPayment.due_date) : null;
        let body = 'Você tem uma cobrança em aberto.';
        if (amountText && dueDateText) {
          body = `${amountText}, com vencimento em ${dueDateText}.`;
        } else if (amountText) {
          body = `${amountText} em aberto.`;
        }
        return {
          modifier: 'warning',
          heading: 'Você tem uma cobrança em aberto',
          body,
          action: payAction(currentPayment) || paymentFallbackAction,
        };
      }
      case 'overdue': {
        const amountText = payload.amount_due != null ? formatPrice(payload.amount_due) : null;
        const dueDateText = currentPayment?.due_date ? formatDate(currentPayment.due_date) : null;
        let body = 'Regularize para manter sua assinatura ativa.';
        if (amountText && dueDateText) {
          body = `${amountText}, vencida em ${dueDateText}. Regularize para manter sua assinatura ativa.`;
        } else if (amountText) {
          body = `${amountText} em atraso. Regularize para manter sua assinatura ativa.`;
        }
        return {
          modifier: 'danger',
          heading: 'Sua cobrança está atrasada',
          body,
          action: payAction(currentPayment) || paymentFallbackAction,
        };
      }
      case 'failed': {
        if (!firstFailed?.next_attempt_at) {
          return suspendedBanner(currentPayment);
        }
        const attemptsText =
          firstFailed.attempts_made != null && firstFailed.attempts_allowed != null
            ? `Tentativa ${firstFailed.attempts_made} de ${firstFailed.attempts_allowed}. `
            : '';
        return {
          modifier: 'danger',
          heading: 'Não conseguimos processar seu pagamento',
          body: `${attemptsText}A próxima tentativa automática acontece em ${formatDate(firstFailed.next_attempt_at)}.`,
          action: payAction(firstFailed) || paymentFallbackAction,
        };
      }
      case 'suspended':
        return suspendedBanner(currentPayment);
      case 'canceled':
        return {
          modifier: 'neutral',
          heading: 'Sua assinatura foi cancelada',
          body: 'Você não receberá novas cobranças.',
          action: payload.resubscribe_url
            ? { label: 'Assinar novamente', to: payload.resubscribe_url }
            : paymentFallbackAction,
        };
      default:
        return {
          modifier: 'neutral',
          heading: 'Não foi possível identificar o status da sua assinatura',
          body: 'Tente novamente em instantes ou entre em contato com o suporte.',
          action: paymentFallbackAction,
        };
    }
  };

  if (error) {
    return isNotFound ? (
      <PublicStatusPage
        className="SubscriberSelfService"
        title="Minha Assinatura"
        merchantName={payload?.merchant?.business_name}
        variant="neutral"
        heading="Não encontramos esta assinatura"
        body="O link pode estar incompleto ou a assinatura pode ter sido removida. Confira o endereço no e-mail que você recebeu."
        action={null}
      />
    ) : (
      <PublicStatusPage
        className="SubscriberSelfService"
        title="Minha Assinatura"
        merchantName={payload?.merchant?.business_name}
        variant="warning"
        heading="Não conseguimos carregar sua assinatura"
        body="Isso costuma ser temporário."
        action={{ label: 'Tentar novamente', onClick: fetchDetail }}
      />
    );
  }

  if (loading) {
    return (
      <PublicStatusPage
        className="SubscriberSelfService"
        title="Minha Assinatura"
        merchantName={payload?.merchant?.business_name}
      >
        <Skeleton active />
      </PublicStatusPage>
    );
  }

  const banner = getBanner();

  return (
    <PublicStatusPage
      className="SubscriberSelfService"
      title="Minha Assinatura"
      variant={banner?.modifier}
      heading={banner?.heading}
      body={banner?.body}
      action={banner?.action}
      merchantName={payload?.merchant?.business_name}
    >
      {payload?.state !== 'canceled' && payload?.open_payments?.length > 0 && (
        <div className="open-payments">
          <h3>Cobranças em Aberto</h3>
          {payload.open_payments.map((p) => (
            <div className="payment-row" key={p.id}>
              <div className="payment-row-main">
                <span className="amount">{formatPrice(p.amount)}</span>
                <span className="due-date">Vencimento: {formatDate(p.due_date)}</span>
              </div>
              <span className={`status-badge ${paymentStatusMeta(p.payment_status).className}`}>
                {paymentStatusMeta(p.payment_status).label}
              </span>
              {p.pay_url && (
                <Link to={p.pay_url} className="payment-row-action">
                  Pagar agora
                </Link>
              )}
            </div>
          ))}
        </div>
      )}

      {payload?.plan && (
        <div className="plan-summary">
          <h3>Plano</h3>
          <div className="plan-card">
            <div className="plan-name">{payload.plan.name}</div>
            {payload.plan.description && <p className="plan-description">{payload.plan.description}</p>}
            <div className="plan-price">
              {formatPrice(payload.plan.amount)}
              <small>/{frequencyNoun(payload.plan.frequency)}</small>
            </div>
          </div>
        </div>
      )}

      {payload?.payment_history?.length > 0 && (
        <div className="payment-history">
          <h3>Histórico de Pagamentos</h3>
          {payload.payment_history.map((p) => {
            const paidDate = p.paid_at || p.due_date;
            const dateLabel = p.paid_at ? 'Pago em' : 'Vencimento';
            return (
              <div className="info-row" key={p.id}>
                <span className="label">
                  {dateLabel}: {formatDate(paidDate)}
                </span>
                <span className="value">{formatPrice(p.amount)}</span>
                <span className={`status-badge ${paymentStatusMeta(p.payment_status).className}`}>
                  {paymentStatusMeta(p.payment_status).label}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {hasFooterContent && (
        <div className="self-service-footer">
          {showCancelLink && (
            <Link to={payload.cancel_url} className="cancel-link">
              Cancelar assinatura
            </Link>
          )}
          {payload?.merchant?.email && (
            <a href={`mailto:${payload.merchant.email}`} className="contact-link">
              Falar com {payload.merchant.business_name || 'o lojista'}
            </a>
          )}
          {payload?.merchant?.phone && (
            <a href={`tel:${payload.merchant.phone}`} className="contact-link">
              Ligar para {payload.merchant.business_name || 'o lojista'}
            </a>
          )}
        </div>
      )}
    </PublicStatusPage>
  );
};

export default SubscriberSelfService;
